import { Injectable } from '@angular/core';
import { EndUserWithId } from '@app/core/interfaces/c5/end-user';
import { LocationGroup } from '@app/core/interfaces/c5/location-group';
import { Site } from '@app/core/interfaces/site';
import { BulkActionEntry } from '@app/shared/models/bulk-action-entry';
import { EnhancedEndUserLite } from '@app/shared/models/enhanced-end-user-lite';
import { FilterableEntry } from '@app/shared/models/filterable-entry';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { SharedService } from '@app/shared/services/shared.service';
import { toMap, uniqBy } from '@app/utils/common';
import { MetaDataProperty, multiOrProperty } from '@app/utils/lists/meta-list/meta-data-property';
import { StringMap } from '@app/utils/string-map';
import { Authorization } from '@business/end-user/models/authorization';
import { combineLatest, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class BulkActionService {
  private endUserMap: StringMap<EnhancedEndUserLite> = {};
  private isMultiLocation: boolean = false;
  private isMultiSite: boolean = false;
  private locationGroupMap: StringMap<LocationGroup> = {};
  private siteMap: StringMap<Site> = {};

  constructor(
    private sharedService: SharedService,
    private enterpriseContextService: EnterpriseContextService
  ) {}

  calculateMetaData(bulkActionEntry: BulkActionEntry<EndUserWithId>): FilterableEntry {
    const endUserLite = this.endUserMap[bulkActionEntry.id];
    const endUser = bulkActionEntry.entry;

    let meta: StringMap<string> = {
      featureProfile: endUserLite.featureProfileName,
    };

    if (this.isMultiLocation && endUserLite.locationId != null) {
      meta.location = this.locationGroupMap[endUserLite.locationId]?.name;
    }

    if (this.isMultiSite) {
      meta.site = this.siteMap[endUserLite.siteId]?.name;
    }

    if (endUserLite.featureProfileName === '_internal') {
      meta.featureProfile = 'featureProfile.internalFeatureProfile';
    } else {
      meta.featureProfile = endUserLite.featureProfileName;
    }

    meta.endUserType = `users.overview.user.${endUser.auth}.info`;

    return {
      key: `${bulkActionEntry.content} ${bulkActionEntry.header}`,
      meta: meta,
    };
  }

  loadFilterProperties(): Observable<MetaDataProperty[]> {
    const endUsers$ = this.enterpriseContextService.endUsers$().pipe(first());
    const featureProfiles$ = this.sharedService
      .fetchAssignedFeatureProfiles()
      .pipe(map((profiles) => profiles.map((profile) => profile.name)));

    const sitesAndLocations$: Observable<[Site[], LocationGroup[]]> =
      this.enterpriseContextService.sitesWithLocations.data$.pipe(
        first(),
        map((sitesWithLocations) => {
          const locations = uniqBy(
            sitesWithLocations.reduce(
              (acc, value) => acc.concat(value.locationGroups),
              [] as LocationGroup[]
            ),
            (group) => group.id
          );
          const sites = sitesWithLocations.map((siteWithLocation) => siteWithLocation.site);

          return [sites, locations];
        })
      );

    return combineLatest([endUsers$, featureProfiles$, sitesAndLocations$]).pipe(
      map(([endUsers, featureProfiles, [sites, locations]]) => {
        this.endUserMap = toMap(endUsers, 'id');
        this.isMultiLocation = locations.length > 1;
        this.isMultiSite = sites.length > 1;
        this.locationGroupMap = toMap(locations, 'id');
        this.siteMap = toMap(sites, 'id');

        const metaDataProperties = [
          multiOrProperty(
            'endUserType',
            'users.overview.type',
            [Authorization.FULL, Authorization.FORBID_DEVICE].map(
              (type) => `users.overview.user.${type}.info`
            )
          ),
          multiOrProperty('featureProfile', 'users.overview.featureProfile', featureProfiles),
        ];

        if (sites.length > 1) {
          metaDataProperties.unshift(
            multiOrProperty(
              'site',
              'acd.overview.filter.site.label',
              sites.map((s) => s.name)
            )
          );
        }

        if (locations.length > 1) {
          metaDataProperties.unshift(
            multiOrProperty(
              'location',
              'users.overview.locationGroup',
              locations.map((locationGroup) => locationGroup.name)
            )
          );
        }

        return metaDataProperties;
      })
    );
  }
}
